/**
 * Storage programs: the room holds one climate until it is stopped. A storage program is a
 * ripewise program with only `ripewise.store` (the room's store settings, no phases); the
 * older `storage` mode (temperature and air offset only) is read the same way.
 */

import type { RipeningProgramDefinition } from "../hooks/useRipeningPrograms";
import { ethyleneDosingFor } from "./ripeningProgramFields";

export type ProgramKind = "ripening" | "storage";

export const PROGRAM_KIND_LABELS: Record<ProgramKind, string> = {
    ripening: "Ripening",
    storage: "Storage",
};

export type StoreValues = Record<string, number>;

/** The store block, in the order it is shown, with the short label used inside it. */
export const STORE_BLOCK_FIELDS = [
    ["Store_Setpoint", "Temperature"],
    ["Store_AirOffset", "Air offset"],
    ["Store_Mode_Aereation", "Ventilation"],
    ["Store_Spv_C2H4", "C2H4"],
    ["Store_Spv_Co2", "CO2"],
    ["Store_Mode_Scrubber", "CO2 scrubber"],
    ["Store_Spv_O2", "O2"],
    ["Store_Mode_N2", "N2 injection"],
] as const;

export type StoreBlockFieldKey = typeof STORE_BLOCK_FIELDS[number][0];

export const storeFieldLabel = (key: string): string => (
    STORE_BLOCK_FIELDS.find(([field]) => field === key)?.[1] ?? key.replace(/_/g, " ")
);

/** A new storage program: no gases controlled until set, ethylene off. */
export const defaultStoreValues = (): StoreValues => ({
    Store_Spv_C2H4: 0,
    Store_Mode_C2H4: 0,
    Store_Mode_Scrubber: 0,
    Store_Mode_N2: 0,
});

export function isStorageDefinition(definition: RipeningProgramDefinition | null | undefined): boolean {
    if (!definition) return false;
    if (definition.mode === "storage") return true;
    const ripewise = definition.ripewise;
    return definition.mode === "ripewise"
        && !(ripewise?.phases?.length)
        && !!ripewise?.store
        && Object.keys(ripewise.store).length > 0;
}

export const programKindOf = (definition: RipeningProgramDefinition | null | undefined): ProgramKind => (
    isStorageDefinition(definition) ? "storage" : "ripening"
);

/** The store settings of a storage program; the older storage mode as store settings. */
export function storeValuesOf(definition: RipeningProgramDefinition | null | undefined): StoreValues {
    if (!definition) return {};
    if (definition.mode === "storage") {
        const values: StoreValues = {};
        if (typeof definition.storage?.setpoint === "number") values.Store_Setpoint = definition.storage.setpoint;
        if (typeof definition.storage?.air_offset === "number") values.Store_AirOffset = definition.storage.air_offset;
        return values;
    }
    return { ...(definition.ripewise?.store ?? {}) };
}

/** Store settings with ethylene dosing following the C2H4 setpoint, as in a phase. */
export function withDerivedStoreEthylene(store: StoreValues): StoreValues {
    return { ...store, Store_Mode_C2H4: ethyleneDosingFor(store.Store_Spv_C2H4) };
}

/** A storage program as it is saved: ripewise with only the store. */
export function storageDefinition(store: StoreValues): RipeningProgramDefinition {
    return { mode: "ripewise", storage: null, ripewise: { store: withDerivedStoreEthylene(store) } };
}

/** N2 injection pushes the oxygen out of the room: nobody may enter while it runs. */
export const storeInjectsN2 = (store: StoreValues | null | undefined): boolean => Number(store?.Store_Mode_N2) === 1;

export const STORE_LOW_OXYGEN = "N2 injection is on while the room stores. The room is dangerous to enter while it runs.";

const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/** What keeps the store settings from being saved or sent; null when they are fine. */
export function validateStoreValues(store: StoreValues): string | null {
    const setpoint = store.Store_Setpoint;
    if (!isNumber(setpoint) || setpoint < -10 || setpoint > 40) return "Storage temperature must be between -10 and 40 °C.";
    const airOffset = store.Store_AirOffset;
    if (airOffset !== undefined && (!isNumber(airOffset) || airOffset < -5 || airOffset > 5)) {
        return "Air offset must be between -5 and 5 °C.";
    }
    if (!isNumber(store.Store_Mode_Aereation)) return "Storage needs a ventilation mode.";
    if (![0, 1].includes(store.Store_Mode_Scrubber)) return "Set the CO2 scrubber on or off.";
    if (![0, 1].includes(store.Store_Mode_N2)) return "Set N2 injection on or off.";
    const negative = (["Store_Spv_C2H4", "Store_Spv_Co2", "Store_Spv_O2"] as const).find((key) => store[key] !== undefined && !(store[key] >= 0));
    if (negative) return `${storeFieldLabel(negative)} must be 0 or more.`;
    return null;
}
